/**
 * My Skills — API wiring.
 *
 * Endpoints (candidate is resolved from the auth token):
 *   GET    /candidates/skills          → list
 *   PUT    /candidates/skills/{id}      → persist edits (rating / months / recency / confirm)
 *   DELETE /candidates/skills/{id}      → remove a skill
 *
 * `api` (src/services/axios.config) carries the bearer token + refresh, and its
 * response interceptor returns the body.
 */
import api from 'src/services/axios.config';
import { skRecencyBand, skStarCount, SK_STAR_TO_LEVEL } from './skillHelpers';

// Base path for the skills resource (GET base, PUT/DELETE base/{id}).
const SKILLS_BASE = '/candidates/skills';

/** Map a raw API row → the shape the page/components use (tolerant of camel/snake). */
export function normalizeSkill(r) {
  return {
    id: r.id,
    name: r.name,
    type: r.type,
    stars: r.stars != null ? r.stars : null,
    proficiency: r.proficiency != null ? r.proficiency : null,
    evidenceMonths:
      r.effective_months ??
      r.evidence_months ??
      r.evidenceMonths ??
      r.months ??
      0,
    inputMonths: r.input_months ?? r.inputMonths ?? null,
    recency: r.recency != null ? r.recency : null,
    is_currently_used: r.is_currently_used ?? false,
    last_used_date: r.last_used_date ?? null,
    is_confirmed: !!r.is_confirmed,
    is_suppressed: !!r.is_suppressed,
    show: r.show != null ? r.show : true,
    context: Array.isArray(r.context) ? r.context : [],
    confidence: r.confidence != null ? r.confidence : null,
  };
}

/** Build the snake_case payload PUT expects from a (page-shaped) skill object. */
export function toApiPayload(s) {
  // Send the *resolved* recency band (the candidate's choice if set, else the
  // value inferred from is_currently_used / last_used_date) — `s.recency` alone
  // is null until a pill is explicitly tapped, even though one looks selected.
  const recency = skRecencyBand(s);
  // Effective experience: the candidate's typed number if set, else the value
  // inferred from work history (what the card actually shows) — `s.inputMonths`
  // alone is null until they edit it, even though a duration is displayed.
  const months =
    s.inputMonths != null ? s.inputMonths : s.evidenceMonths || null;
  // Effective rating: the candidate's tapped stars if set, else the value
  // inferred from proficiency (what the card actually shows) — `s.stars` alone
  // is null until a star is explicitly tapped, even though stars look filled.
  const stars = skStarCount(s) || null;
  const proficiency = s.proficiency ?? (stars ? SK_STAR_TO_LEVEL[stars] : null);
  // is_confirmed / is_currently_used / is_suppressed are derived server-side and
  // intentionally omitted from the payload.
  return {
    stars,
    proficiency,
    input_months: months,
    recency: recency ?? null,
  };
}

export async function fetchSkills() {
  const res = await api.get(SKILLS_BASE);
  const list = Array.isArray(res)
    ? res
    : res?.skills || res?.data?.skills || res?.data || [];
  return list.map(normalizeSkill);
}

/**
 * GET /candidates/skills/unconfirmed → whether any skills still await review and
 * how many. Drives the "Update your skill details" boost card on the dashboard.
 * Response field names vary, so read tolerantly (flag + count, camel/snake, or
 * nested under `data`); fall back to count > 0 when no explicit flag is sent.
 */
export async function fetchUnconfirmedSkills() {
  const res = await api.get(`${SKILLS_BASE}/unconfirmed`);
  const body = res?.data ?? res ?? {};
  const rawCount =
    body.count ?? body.skills_count ?? body.unconfirmedCount ?? 0;
  const count = Number.isFinite(Number(rawCount)) ? Number(rawCount) : 0;
  const flag =
    body.skills_exist ??
    body.skillsExist ??
    body.has_unconfirmed ??
    body.hasUnconfirmed ??
    body.unconfirmed ??
    null;
  const hasUnconfirmed = flag != null ? !!flag : count > 0;
  return { hasUnconfirmed, count };
}

export function updateSkill(id, body) {
  return api.put(`${SKILLS_BASE}/${id}`, body);
}

export function deleteSkill(id) {
  return api.delete(`${SKILLS_BASE}/${id}`);
}
